import * as ToastPrimitive from "@radix-ui/react-toast";
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

/**
 * Shared UI primitives — SPEC §12 permits Radix.
 *
 * Radix rather than hand-rolled because the parts that are easy to get wrong
 * are the parts it does: focus trapping, focus restore, Escape handling,
 * `aria-modal`, and scroll locking. Every field and control here is sized for
 * a 48px tap target (SPEC §12) because most of these screens are used on a
 * phone, on a site, one-handed.
 */

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

export const ui = {
  input:
    "w-full min-h-[48px] rounded-xl border border-line bg-surface px-3.5 py-2.5 text-base text-ink " +
    "placeholder:text-faint focus:border-ink focus:outline-none focus:ring-2 focus:ring-hiviz/40 " +
    "disabled:opacity-50",
  label: "block text-xs font-semibold uppercase tracking-wide text-muted",
  card: "rounded-2xl border border-line bg-surface p-4",
  btn: "tap-target inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors disabled:opacity-40 disabled:pointer-events-none",
  primary: "bg-ink text-white hover:bg-ink/90",
  secondary: "border border-line bg-surface text-ink hover:bg-sunk",
  danger: "text-absent hover:bg-absent/10",
  ghost: "text-muted hover:text-ink",
} as const;

// ---------------------------------------------------------------------------
// Field
// ---------------------------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | undefined;
  children: (id: string) => ReactNode;
}): ReactNode {
  const id = useId();
  return (
    <div className="space-y-1">
      <label htmlFor={id} className={ui.label}>
        {label}
      </label>
      {children(id)}
      {hint !== undefined && error === undefined ? (
        <p className="text-xs text-faint">{hint}</p>
      ) : null}
      {/* Announced, not merely coloured — colour alone is not an error report. */}
      {error !== undefined ? (
        <p role="alert" className="text-xs font-medium text-absent">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

type ToastTone = "success" | "error" | "info";
interface ToastItem {
  id: number;
  title: string;
  detail?: string;
  tone: ToastTone;
}

const ToastContext = createContext<{
  push: (t: Omit<ToastItem, "id">) => void;
} | null>(null);

export function useToast(): (t: Omit<ToastItem, "id">) => void {
  const ctx = useContext(ToastContext);
  if (ctx === null) throw new Error("useToast used outside ToastProvider");
  return ctx.push;
}

const TONE: Record<ToastTone, string> = {
  success: "border-present/30 bg-surface text-ink",
  error: "border-absent/40 bg-surface text-ink",
  info: "border-line bg-surface text-ink",
};

const TONE_BAR: Record<ToastTone, string> = {
  success: "bg-present",
  error: "bg-absent",
  info: "bg-ink",
};

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((t: Omit<ToastItem, "id">) => {
    setItems((prev) => [...prev, { ...t, id: Date.now() + Math.random() }]);
  }, []);
  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right" duration={5000}>
        {children}
        {items.map((item) => (
          <ToastPrimitive.Root
            key={item.id}
            onOpenChange={(open) => {
              if (!open) setItems((p) => p.filter((x) => x.id !== item.id));
            }}
            className={`flex gap-3 overflow-hidden rounded-xl border px-4 py-3 text-sm shadow-lg shadow-black/10 ${TONE[item.tone]}`}
          >
            <span
              aria-hidden="true"
              className={`-my-3 -ml-4 w-1 shrink-0 ${TONE_BAR[item.tone]}`}
            />
            <div>
              <ToastPrimitive.Title className="font-semibold">
                {item.title}
              </ToastPrimitive.Title>
              {item.detail !== undefined ? (
                <ToastPrimitive.Description className="mt-0.5 text-xs text-muted">
                  {item.detail}
                </ToastPrimitive.Description>
              ) : null}
            </div>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-0 right-0 z-50 flex w-full max-w-sm flex-col gap-2 p-4 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Loading / empty states
// ---------------------------------------------------------------------------

export function Skeleton({ rows = 3 }: { rows?: number }): ReactNode {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="h-9 animate-pulse rounded-lg bg-sunk"
          style={{ width: `${100 - i * 8}%` }}
        />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className="rounded-2xl border border-dashed border-line px-4 py-8 text-center">
      <p className="text-sm font-semibold text-ink">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-xs text-muted">{body}</p>
      {action !== undefined ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/** A query that failed should say so and offer a way back, not render blank. */
export function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}): ReactNode {
  const message =
    error instanceof Error ? error.message : "Something went wrong";
  return (
    <div
      role="alert"
      className="rounded-2xl border border-absent/30 bg-surface px-4 py-3 text-sm"
    >
      <p className="font-semibold text-absent">Could not load this</p>
      <p className="mt-1 text-xs text-muted">{message}</p>
      {onRetry !== undefined ? (
        <button
          type="button"
          onClick={onRetry}
          className={`${ui.btn} ${ui.secondary} mt-3`}
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}

/**
 * A person's name with their employee code beside it.
 *
 * The code is set in smaller, quieter type: it identifies, it does not compete
 * with the name. Rendered as one element so every list, card and header shows
 * the pair the same way rather than each screen inventing its own layout —
 * which is how it ended up on the directory and nowhere a supervisor works.
 */
export function PersonName({
  name,
  employeeCode,
  className = "",
}: {
  name: string;
  employeeCode?: string | null;
  className?: string;
}): ReactNode {
  return (
    <span className={className}>
      {name}
      {employeeCode == null || employeeCode === "" ? null : (
        <span className="ml-1.5 text-xs font-normal tabular-nums text-muted">
          {employeeCode}
        </span>
      )}
    </span>
  );
}

export function Money({ fils }: { fils: number }): ReactNode {
  return <span className="tabular-nums">{(fils / 100).toFixed(2)}</span>;
}
